/**
 * listening-exam-launch — 英語聽力卷的選卷 / 動作分流(複習中心接點,spec §7.5)。
 *
 * 流程:複習中心「英語聽力卷」卡 → 本畫面:
 *   - 「出一份新的(重抽)」:換 seed 用產生器 `generateListeningExam` 生新卷。
 *   - 「Review 2 範例」:定稿 golden `HENLIN_REVIEW_2`。
 *   選卷後 →「線上作答」`runListeningExam` /「列印 A4」開新視窗。
 *
 * 視覺 = 學術風格設計系統(pageShell / academicCard / academicButton),專業介面
 * (無音效 / mascot / 彩光)。學習/遊戲分離:純學習側,絕不碰 commanderXp / 戰鬥數值。
 */

import { pageShell, academicCard, academicButton } from '../ui/components';
import { mountEscapeButton } from '../escape-button';
import { generateListeningExam } from './listening-exam-gen';
import { HENLIN_REVIEW_2 } from './listening-exam-data';
import { runListeningExam } from './listening-exam';
import type { ListeningExam } from './listening-exam-types';
import { generateListeningExamHtml } from '../printable/pdf-gen';

/** 列印一份聽力卷:開新視窗 write A4 HTML(沿用 pdf-gen 的 window.open 模式)。 */
function openPrintWindow(exam: ListeningExam): void {
  const html = generateListeningExamHtml(exam);
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
}

/** 用時間戳當 seed 重抽一份新卷(可重現:同 seed 同卷)。 */
function freshSeed(): number {
  return Math.floor(Date.now() % 1_000_000);
}

/**
 * Render 聽力卷選卷畫面 into `container`。
 *
 * @param container 掛載點。
 * @param onDone    離開回呼(回複習中心)。
 */
export function runListeningExamLaunch(
  container: HTMLElement,
  onDone: () => void,
): void {
  renderMenu(container, onDone);
}

function renderMenu(container: HTMLElement, onDone: () => void): void {
  container.innerHTML = '';

  const shell = pageShell(container, {
    title: '英語聽力卷',
    subtitle: '聽發音作答的英語段考型聽力卷。可線上作答或列印 A4 紙卷。',
  });

  const unmountEscape = mountEscapeButton(() => {
    unmountEscape();
    onDone();
  });

  const root = document.createElement('div');
  root.className = 'lx-launch';
  root.style.cssText = 'display:flex;flex-direction:column;gap:14px;';
  shell.body.appendChild(root);

  // 兩張卡:重抽新卷 / Review 2 範例。
  const newCard = academicCard({
    icon: '🎲',
    label: '出一份新的(重抽)',
    sub: '從題庫池隨機生一份同型聽力卷(每次不同)',
    onClick: () => {
      unmountEscape();
      const exam = generateListeningExam({ seed: freshSeed() });
      renderExamActions(container, exam, onDone);
    },
  });
  newCard.id = 'lx-launch-new';
  newCard.classList.add('lx-launch-card');

  const goldenCard = academicCard({
    icon: '📄',
    label: 'Review 2 範例',
    sub: '翰林英語段考型定稿聽力卷(jobs / 家庭 / Wh- 問句)',
    onClick: () => {
      unmountEscape();
      renderExamActions(container, HENLIN_REVIEW_2, onDone);
    },
  });
  goldenCard.id = 'lx-launch-golden';
  goldenCard.classList.add('lx-launch-card');

  root.append(newCard, goldenCard);
}

/** 選卷後:線上作答 / 列印 的動作頁。 */
function renderExamActions(
  container: HTMLElement,
  exam: ListeningExam,
  onDone: () => void,
): void {
  container.innerHTML = '';

  const shell = pageShell(container, {
    title: exam.title,
    subtitle: '選擇線上作答(即時計分)或列印 A4 紙卷(筆寫、附解答頁)。',
  });

  const back = (): void => renderMenu(container, onDone);
  const unmountEscape = mountEscapeButton(() => {
    unmountEscape();
    back();
  });

  const root = document.createElement('div');
  root.className = 'lx-actions-page';
  root.style.cssText = 'display:flex;flex-wrap:wrap;gap:12px;';
  shell.body.appendChild(root);

  const onlineBtn = academicButton({
    label: '線上作答',
    variant: 'primary',
    onClick: () => {
      unmountEscape();
      runListeningExam(container, exam, () => renderExamActions(container, exam, onDone));
    },
  });
  onlineBtn.id = 'lx-action-online';

  const printBtn = academicButton({
    label: '🖨️ 列印 A4',
    variant: 'secondary',
    onClick: () => openPrintWindow(exam),
  });
  printBtn.id = 'lx-action-print';

  const backBtn = academicButton({
    label: '← 換一份',
    variant: 'secondary',
    onClick: () => {
      unmountEscape();
      back();
    },
  });
  backBtn.id = 'lx-action-back';

  root.append(onlineBtn, printBtn, backBtn);
}
